import { QueryOptions, useApolloClient } from '@apollo/client';
import { GET_CUSTOMER } from 'graphql/customers/queries';
import {
  GET_VACANCY,
  GET_VACANCY_ADS,
  GET_VACANCY_BUDGET,
  GET_VACANCY_METRICS,
} from 'graphql/vacancies/queries';
import { ToastTypes } from 'types/notificationTypes';
import useNavigationContext from './context/nav-context';
import { useToastNotifications } from './notificationHooks';

/** Reconcile a checkout return using reads, preserving the mounted editor and its draft. */
export const useCheckoutRecovery = () => {
  const client = useApolloClient();
  const { activeCustomer, activeVacancy } = useNavigationContext();
  const { addToast } = useToastNotifications();

  return async (): Promise<void> => {
    const context = { headers: { 'Active-Customer': activeCustomer } };
    const reads: QueryOptions[] = [
      { query: GET_CUSTOMER, variables: { customerId: activeCustomer } },
      {
        query: GET_VACANCY,
        variables: {
          id: activeVacancy,
          include: 'brand.facebookPage,targeting',
        },
      },
      { query: GET_VACANCY_BUDGET, variables: { vacancyId: activeVacancy } },
    ];
    // These network queries update the shared cache without putting the navigation
    // provider into its loading branch and unmounting the unsaved editor.
    const refreshes: Promise<unknown>[] = reads.map((read) =>
      Promise.resolve().then(() => {
        if (!activeCustomer || !activeVacancy)
          throw new Error('No active campaign is available to refresh.');
        return client.query({ ...read, context, fetchPolicy: 'network-only' });
      })
    );
    // Only refresh visible, read-only campaign displays. Never call a form submission
    // callback or refetch editor data, which could replace unsaved changes.
    if (activeCustomer && activeVacancy)
      client.getObservableQueries('active').forEach((query) => {
        if (
          query.hasObservers() &&
          (query.options.query === GET_VACANCY_ADS ||
            query.options.query === GET_VACANCY_METRICS) &&
          query.options.variables?.vacancyId === activeVacancy
        )
          refreshes.push(Promise.resolve().then(() => query.refetch()));
      });
    const results = await Promise.allSettled(refreshes);
    const failed = results.some(({ status }) => status === 'rejected');
    addToast({
      title: 'Returned from checkout',
      description: failed
        ? 'Some campaign information could not be refreshed. Refresh the page before making another payment.'
        : 'Your campaign information has been refreshed. Review your current budget and balance before making another payment.',
      type: failed ? ToastTypes.WARNING : ToastTypes.INFO,
    });
  };
};
